import { StyleSheet, UnistylesRuntime } from "react-native-unistyles";

import CustomPlatform from "$src/core/utils/platform/platform";

// The height the hero should occupy: capped to a sensible aspect ratio of
// the viewport, ~90% of the available height, and an absolute maximum.
// `IMAGE_DIMENSIONS.hero` requests exactly this, so the two cannot drift: asking
// for the portrait ratio on a landscape screen over-fetched a full-screen image
// by ~45% (1920x1408 for a box that caps at 1920x972 on tvOS).
const getHeroHeight = ({
  screen,
  insets,
  maxHeight,
}: {
  screen: { width: number; height: number };
  insets: { top: number; bottom: number };
  maxHeight: number;
}) =>
  Math.min(
    screen.height > screen.width
      ? screen.width * (8 / 6) // 6:8 aspect ratio on a portrait display
      : screen.width * (9 / 16), // 16:9 aspect ratio on a landscape display
    (screen.height - insets.top - insets.bottom) * 0.9, // ~90% of the screen height
    maxHeight,
  );

const stylesheet = StyleSheet.create(({ spacing }, { insets, screen }) => {
  const heroHeight = getHeroHeight({
    screen,
    insets,
    maxHeight: spacing(220),
  });

  return {
    contentContainer: ({
      tabBarHeight,
      hasHero,
    }: {
      tabBarHeight: number;
      hasHero: boolean;
    }) => ({
      paddingTop: CustomPlatform.select({
        web: {
          lg: hasHero ? 0 : spacing(36),
        },
        // Android TV's 960dp canvas sits on `md`, so an `lg` key never applies there.
        tv: {
          xs: hasHero ? 0 : spacing(28),
        },
        default: {
          xs: hasHero ? 0 : insets.top + spacing(6),
        },
      }),
      paddingBottom:
        spacing(8) +
        CustomPlatform.select({
          web: tabBarHeight,
          native: tabBarHeight,
          default: 0,
        }),
    }),
    hero: {
      alignItems: "center",
      width: "100%",
      margin: "auto",
      maxHeight: { xs: heroHeight },
      // On web `height: "100%"` (used throughout the Hero) has no definite
      // ancestor height to resolve against, so the hero collapses to its text
      // content. Derive the height from the (definite) width via a CSS aspect
      // ratio instead — fluid with no JS-measured pixels — and cap it to the
      // viewport. Native keeps the measured `maxHeight` above.
      _web: {
        aspectRatio: 16 / 9,
        maxHeight: "90vh",
      },
    },
  };
});

const initialScreen = UnistylesRuntime.screen;
const initialTheme = UnistylesRuntime.getTheme();

// Android TV floats its tab bar over the content, so the focus-scroll must clear it.
export const FOCUS_SCROLL_INSET = CustomPlatform.select({
  androidTV: { start: initialTheme.spacing(22), end: 0 },
  default: undefined,
});

export const IMAGE_DIMENSIONS = {
  hero: [
    {
      width: initialScreen.width,
      height: Math.round(
        getHeroHeight({
          screen: initialScreen,
          insets: UnistylesRuntime.insets,
          maxHeight: initialTheme.spacing(220),
        }),
      ),
    },
  ],
};

export default stylesheet;
